import type { IChartData } from "../../interfaces";

export type TrendChartTimeValue = Date | string | null | undefined;

export interface ITrendChartTimeBounds {
  start: TrendChartTimeValue;
  end: TrendChartTimeValue;
}

interface IResolveTrendChartTimeDomainArgs {
  data: IChartData[];
  fallbackStart: Date | string;
  fallbackEnd: Date | string;
  timeBounds?: ITrendChartTimeBounds | null;
}

interface ITrendChartTimeDomain {
  minDate: Date;
  maxDate: Date;
}

const normalizeChartDateString = (value: string): string => value.replace("Z", "");

const timeValueToChartDateString = (value: Date | string): string =>
  value instanceof Date ? normalizeChartDateString(value.toISOString()) : normalizeChartDateString(value);

const timeValueToDate = (value: TrendChartTimeValue): Date | null => {
  if (!value) {
    return null;
  }

  const date = new Date(timeValueToChartDateString(value));

  return Number.isFinite(date.getTime()) ? date : null;
};

const resolveDataBounds = (data: IChartData[]): { min: string | null; max: string | null } => {
  let minDate: string | null = null;
  let maxDate: string | null = null;
  let minTimestamp: number | null = null;
  let maxTimestamp: number | null = null;

  data.forEach(({ items }) => {
    items.forEach(({ date }) => {
      const dateString = normalizeChartDateString(date);
      const timestamp = new Date(dateString).getTime();

      if (!Number.isFinite(timestamp)) {
        return;
      }

      if (minTimestamp === null || timestamp < minTimestamp) {
        minDate = dateString;
        minTimestamp = timestamp;
      }

      if (maxTimestamp === null || timestamp > maxTimestamp) {
        maxDate = dateString;
        maxTimestamp = timestamp;
      }
    });
  });

  return {
    min: minDate,
    max: maxDate,
  };
};

const applyPadding = (minDateRaw: Date, maxDateRaw: Date): ITrendChartTimeDomain => {
  const diffMs = maxDateRaw.getTime() - minDateRaw.getTime();
  const paddingMs = diffMs * 0.05;

  return {
    minDate: new Date(minDateRaw.getTime() - paddingMs),
    maxDate: new Date(maxDateRaw.getTime() + paddingMs),
  };
};

export const resolveTrendChartTimeDomain = ({
  data,
  fallbackStart,
  fallbackEnd,
  timeBounds,
}: IResolveTrendChartTimeDomainArgs): ITrendChartTimeDomain => {
  if (timeBounds) {
    const startDate = timeValueToDate(timeBounds.start);
    const endDate = timeValueToDate(timeBounds.end);

    if (startDate && endDate) {
      return {
        minDate: startDate.getTime() <= endDate.getTime() ? startDate : endDate,
        maxDate: startDate.getTime() <= endDate.getTime() ? endDate : startDate,
      };
    }
  }

  const dataBounds = resolveDataBounds(data);
  const minDateRaw = timeValueToDate(dataBounds.min ?? fallbackStart) ?? new Date(0);
  const maxDateRaw = timeValueToDate(dataBounds.max ?? fallbackEnd) ?? minDateRaw;

  return applyPadding(minDateRaw, maxDateRaw);
};
